
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import PsychologistCardHeader from './psychologist-card/PsychologistCardHeader';
import PsychologistCardRating from './psychologist-card/PsychologistCardRating';
import PsychologistCardSpecialties from './psychologist-card/PsychologistCardSpecialties';
import PsychologistCardLanguages from './psychologist-card/PsychologistCardLanguages';
import PsychologistCardDurations from './psychologist-card/PsychologistCardDurations';
import PsychologistCardActions from './psychologist-card/PsychologistCardActions';
import { PsychologistCardProps } from './psychologist-card/types';

const PsychologistCard: React.FC<PsychologistCardProps> = ({
  psychologist,
  variant = 'home',
  isSelected = false,
  onSelect,
  onBookClick,
  onViewProfile,
  onViewFullProfile,
  onManageClick,
  showViewProfile = true,
  showBooking = true,
  showManagement = false
}) => {
  const navigate = useNavigate();

  const handleBookClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onBookClick) {
      onBookClick(psychologist.id);
    } else {
      navigate(`/schedule/step1?psychologistId=${psychologist.id}`);
    }
  };

  const handleViewProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewProfile) {
      onViewProfile(psychologist.id);
    }
  };

  const handleViewFullProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewFullProfile) {
      onViewFullProfile(psychologist.id);
    } else {
      navigate(`/psychologist-details/${psychologist.id}`);
    }
  };

  const handleManageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onManageClick) {
      onManageClick(psychologist.id);
    }
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(psychologist.id);
    }
  };

  const renderSummary = () => {
    const summary = psychologist.short_bio || psychologist.bio;
    
    return summary && (
      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
        {summary}
      </p>
    );
  };

  const renderStatusBadge = () => {
    if (variant !== 'admin' || !psychologist.status) return null;

    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pendente' },
      approved: { color: 'bg-green-100 text-green-800', text: 'Aprovado' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejeitado' },
      suspended: { color: 'bg-gray-100 text-gray-800', text: 'Suspenso' }
    };

    const statusStyle = statusConfig[psychologist.status as keyof typeof statusConfig];
    
    if (!statusStyle) return null;
    
    return (
      <div className="mb-3">
        <span className={`px-2 py-1 text-xs rounded-full ${statusStyle.color}`}>
          {statusStyle.text}
        </span>
      </div>
    );
  };

  const getCardClassName = () => {
    const baseClass = "hover:shadow-lg transition-shadow duration-200";
    const selectedClass = isSelected ? "ring-2 ring-primary" : "";
    
    switch (variant) {
      case 'compact':
        return `${baseClass} ${selectedClass} h-auto`;
      case 'admin':
        return `${baseClass} ${selectedClass} border-l-4 border-l-blue-500`;
      case 'booking':
        return `${baseClass} ${selectedClass} cursor-pointer`;
      default:
        return `${baseClass} ${selectedClass}`;
    }
  };

  return (
    <Card className={getCardClassName()} onClick={handleCardClick}>
      <CardContent className="p-6">
        <PsychologistCardHeader psychologist={psychologist} />
        <PsychologistCardRating psychologist={psychologist} />
        {renderSummary()}
        <PsychologistCardSpecialties psychologist={psychologist} />
        <PsychologistCardLanguages psychologist={psychologist} />
        {renderStatusBadge()}
        {variant !== 'compact' && (
          <PsychologistCardDurations psychologist={psychologist} />
        )}
        <PsychologistCardActions
          variant={variant}
          showViewProfile={showViewProfile}
          showBooking={showBooking}
          showManagement={showManagement}
          onViewProfile={handleViewProfile}
          onViewFullProfile={handleViewFullProfile}
          onBookClick={handleBookClick}
          onManageClick={handleManageClick}
        />
      </CardContent>
    </Card>
  );
};

export default PsychologistCard;
